import { useState, useEffect, ReactNode } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Trash2, ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';

// Helper to format relative time
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

interface SpaceBlockProps {
  id: string;
  title: string;
  icon?: ReactNode;
  isExpanded: boolean;
  createdAt?: Date;
  onToggle: () => void;
  onTitleChange?: (title: string) => void;
  onDelete?: () => void;
  onOpenInTab?: () => void;
  children: ReactNode;
  dragHandleProps?: Record<string, unknown>;
}

export const SpaceBlock = ({
  id,
  title,
  icon,
  isExpanded,
  createdAt,
  onToggle,
  onTitleChange,
  onDelete,
  onOpenInTab,
  children,
  dragHandleProps,
}: SpaceBlockProps) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(title);

  // Sync title state when prop changes
  useEffect(() => {
    setTitleValue(title);
  }, [title]);

  const handleTitleSave = () => {
    if (titleValue.trim() && titleValue !== title) {
      onTitleChange?.(titleValue.trim());
    } else {
      setTitleValue(title);
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTitleValue(title);
      setIsEditingTitle(false);
    }
  };

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Absolute Drag handle - appears on hover outside the block */}
      <div
        className={`absolute -left-7 top-4 transition-opacity duration-150 ${isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        {...dragHandleProps}
      >
        <div className="p-1 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400">
          <GripVertical className="w-4 h-4" />
        </div>
      </div>

      {/* Absolute Delete button - appears on hover outside the block */}
      <div
        className={`absolute -right-7 top-4 transition-opacity duration-150 ${isHovered ? 'opacity-100' : 'opacity-0'
          }`}
      >
        {onDelete && (
          <button
            onClick={onDelete}
            className="p-1 text-slate-300 hover:text-red-400 transition-colors"
            title="Delete block"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="bg-[#FAFAF9] rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
        {/* Block Header */}
        <div className="flex items-center gap-3 p-4">
          <button
            onClick={onToggle}
            className="p-1 hover:bg-slate-100 rounded transition-colors flex-shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </button>

          {icon && (
            <div className="flex-shrink-0 flex items-center">
              {icon}
            </div>
          )}

          <div className="flex-1 min-w-0 flex items-center">
            {isEditingTitle ? (
              <Input
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={handleTitleKeyDown}
                className="h-8 text-sm font-medium border-slate-200 w-full focus-visible:ring-1 focus-visible:ring-slate-300 focus-visible:ring-offset-0"
                autoFocus
              />
            ) : (
              <button
                onClick={() => setIsEditingTitle(true)}
                className="text-left w-full hover:text-slate-600 transition-colors flex items-center"
              >
                <span className="text-sm font-medium text-slate-700 truncate">
                  {title}
                </span>
              </button>
            )}
          </div>

          {/* Time & Mobile Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {createdAt && (
              <span className="text-[10px] text-slate-300 font-medium">
                {formatRelativeTime(createdAt)}
              </span>
            )}

            {/* Show actions inside block only on mobile */}
            <div className="flex items-center gap-1 md:hidden">
              {onOpenInTab && (
                <button
                  onClick={onOpenInTab}
                  className="p-1.5 text-slate-400"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={onDelete}
                  className="p-1.5 text-slate-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* In-block Open in Tab for Desktop (replaces link in mobile menu) */}
            <div className="hidden md:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onOpenInTab && (
                <button
                  onClick={onOpenInTab}
                  className="p-1.5 hover:bg-blue-50 rounded transition-colors text-slate-400 hover:text-blue-500"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Block Content */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t border-slate-50">
            <div className="w-full">
              {children}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

